const HELP_LINKS = {
  quickstart: "/app/help/quickstart.html",
  dailyOperations: "/app/help/daily-operations.html",
  roleWalkthroughs: "/app/help/role-walkthroughs.html",
  supportPlaybook: "/app/help/support-playbook.html",
};

function filterCount(filters = {}) {
  return Object.entries(filters).filter(([key, value]) => {
    if (key === "status") {
      return value && value !== "open";
    }
    return Boolean(value);
  }).length;
}

export function helpLinks() {
  return Object.assign({}, HELP_LINKS);
}

export function buildSurfaceHelp({ surfaceKey, role, context = {} }) {
  const links = helpLinks();
  const roleLabel = role === "tenant_owner" ? "Owner" : role === "manager" ? "Manager" : "Operator";
  if (surfaceKey === "manager") {
    return {
      eyebrow: "Embedded guidance",
      title: "Manager playbook",
      description: "Supervisory guidance that keeps rebalancing and risk review inside the product.",
      cards: [
        {
          label: "Run the fastest queue review",
          detail: "Start in Manager Brief, confirm the at-risk queue, then use Workload Radar before taking any bulk action.",
          tone: "ready",
          actionLabel: "Open role walkthroughs",
          href: links.roleWalkthroughs,
        },
        {
          label: "Escalate only after inspection",
          detail: "Open the latest record from Outcome Board or Queue Activity Focus before assigning or escalating a record in bulk.",
          tone: "stale",
          actionLabel: "Open daily operations",
          href: links.dailyOperations,
        },
        {
          label: `${context.outcomeCount || 0} outcome signals this week`,
          detail: "Use the outcome focus and reporting readout together when the queue looks healthy but conversion posture feels off.",
          tone: context.outcomeCount ? "ready" : "muted",
          actionLabel: "Open quickstart",
          href: links.quickstart,
        },
      ],
      footer: `${roleLabel} workflows are densest here on purpose. Keep the shell clean and use this surface for inspection plus action.`,
    };
  }
  if (surfaceKey === "operator") {
    return {
      eyebrow: "Embedded guidance",
      title: "Operator workflow",
      description: "A quick reminder of the intended day-to-day loop inside the CRM.",
      cards: [
        {
          label: "Start with the focus queue",
          detail: "Work the focus queue first, then move to Mine, Unassigned, or Overdue depending on where the backlog is accumulating.",
          tone: "ready",
          actionLabel: "Open daily operations",
          href: links.dailyOperations,
        },
        {
          label: "Stay inside the record workspace",
          detail: "Use the action dock, follow-up tasks, internal notes, and record progression without leaving the conversation detail.",
          tone: "ready",
          actionLabel: "Open role walkthroughs",
          href: links.roleWalkthroughs,
        },
        {
          label: "Use reminders instead of memory",
          detail: "If you cannot resolve a record now, create a reminder so the next touch stays visible to the team.",
          tone: "stale",
          actionLabel: "Open quickstart",
          href: links.quickstart,
        },
      ],
      footer: "Operators should be able to claim, reply, note, and set reminders without dropping out of the inbox loop.",
    };
  }
  if (surfaceKey === "admin") {
    return {
      eyebrow: "Embedded guidance",
      title: "Owner setup and rollout",
      description: "Use this surface as the control room for onboarding, policy, reporting, and launch readiness.",
      cards: [
        {
          label: "Complete onboarding before deeper tuning",
          detail: "Finish the Guided Onboarding checklist first. It sets the fastest path to a safe go-live state.",
          tone: context.onboardingStatus === "complete" ? "ready" : "stale",
          actionLabel: "Open quickstart",
          href: links.quickstart,
        },
        {
          label: "Treat reporting as a management loop",
          detail: "Review reporting readout after policy changes so you can see whether queue changes improved actual outcomes.",
          tone: "ready",
          actionLabel: "Open role walkthroughs",
          href: links.roleWalkthroughs,
        },
        {
          label: "Keep support docs nearby for launch",
          detail: "The support playbook is the fastest way to troubleshoot smoke posture, incidents, or delivery failures during rollout.",
          tone: "muted",
          actionLabel: "Open support playbook",
          href: links.supportPlaybook,
        },
      ],
      footer: "Owners should be able to configure the tenant, review readiness, and train the team from this workspace alone.",
    };
  }
  if (surfaceKey === "support") {
    return {
      eyebrow: "Embedded guidance",
      title: "Support response guide",
      description: "Fastest next steps when a tenant reports delivery issues, smoke failures, or workflow drift.",
      cards: [
        {
          label: "Start with the selected destination",
          detail: "Inspect history, run a test, then recover only when the quarantine or failure state clearly supports it.",
          tone: "ready",
          actionLabel: "Open support playbook",
          href: links.supportPlaybook,
        },
        {
          label: "Use governance for cross-surface context",
          detail: "If worker health, smoke posture, or config changes look related, jump to Health instead of troubleshooting delivery in isolation.",
          tone: "stale",
          actionLabel: "Open role walkthroughs",
          href: links.roleWalkthroughs,
        },
        {
          label: `${context.destinationCount || 0} destinations visible`,
          detail: "Empty destination state is valid on starter plans. Support should still review incidents and telemetry before concluding nothing is wrong.",
          tone: context.destinationCount ? "ready" : "muted",
          actionLabel: "Open quickstart",
          href: links.quickstart,
        },
      ],
      footer: "Support should always leave this surface with a clear next step: inspect, test, recover, or escalate to governance.",
    };
  }
  if (surfaceKey === "governance") {
    return {
      eyebrow: "Embedded guidance",
      title: "Owner health review",
      description: "This surface is for risk reduction, not daily workflow. Review it when releases change, incidents spike, or smoke posture drifts.",
      cards: [
        {
          label: "Start with posture, then worker, then config",
          detail: "That order keeps release, worker, and tenant drift review consistent from one session to the next.",
          tone: "ready",
          actionLabel: "Open quickstart",
          href: links.quickstart,
        },
        {
          label: "Use incidents to decide where to go next",
          detail: "Incidents often tell you whether to return to Admin, move into Support, or refresh the current governance view.",
          tone: "stale",
          actionLabel: "Open support playbook",
          href: links.supportPlaybook,
        },
        {
          label: `${context.incidentCount || 0} recent incidents`,
          detail: "A quiet incident board does not eliminate risk. Check release diagnostics and recent config changes before declaring the tenant stable.",
          tone: context.incidentCount ? "overdue" : "muted",
          actionLabel: "Open role walkthroughs",
          href: links.roleWalkthroughs,
        },
      ],
      footer: "Governance should explain tenant health in minutes, not force owners to reconstruct it from several screens.",
    };
  }
  return {
    eyebrow: "Embedded guidance",
    title: "CRM help",
    description: "Role-specific guidance is unavailable for this surface.",
    cards: [],
    footer: "",
  };
}

export function buildInboxEmptyState({ surfaceKey, activeView, filters }) {
  const links = helpLinks();
  const activeFilterCount = filterCount(filters);
  const roleLabel = surfaceKey === "manager" ? "manager" : "operator";
  const resetAction = activeFilterCount
    ? {
        label: "Reset filters",
        dataset: { resetInboxFilters: surfaceKey },
        kind: "button",
      }
    : null;
  const docAction = {
    label: roleLabel === "manager" ? "Open role walkthroughs" : "Open daily operations",
    href: roleLabel === "manager" ? links.roleWalkthroughs : links.dailyOperations,
    kind: "link",
  };
  return {
    title: `No records in ${activeView.label}`,
    detail:
      activeFilterCount > 0
        ? `No queue records matched the current ${roleLabel} filters. Reset the filters or switch to a different queue slice.`
        : `There are no queue records in ${activeView.label.toLowerCase()} right now. Switch slices or use the linked guidance to review the intended workflow.`,
    tone: activeFilterCount > 0 ? "stale" : "ready",
    actions: [resetAction, docAction].filter(Boolean),
  };
}

export function buildDetailPlaceholder(surfaceKey) {
  const links = helpLinks();
  if (surfaceKey === "manager") {
    return {
      title: "Open a queue record to inspect it",
      detail: "Use Inbox Workspace or Outcome Board to load the full thread before rebalancing ownership or taking bulk action.",
      tone: "muted",
      actions: [{ label: "Open role walkthroughs", href: links.roleWalkthroughs, kind: "link" }],
    };
  }
  return {
    title: "Select a conversation to start work",
    detail: "The detail workspace is where replies, reminders, notes, and record progression all stay together.",
    tone: "muted",
    actions: [{ label: "Open daily operations", href: links.dailyOperations, kind: "link" }],
  };
}

export function buildReportingEmptyState() {
  return {
    title: "No reporting buckets matched these filters",
    detail: "Widen the reporting range or reset the filters, then use Reporting Readout to compare volume, response, and outcomes.",
    tone: "stale",
    actions: [{ label: "Open role walkthroughs", href: HELP_LINKS.roleWalkthroughs, kind: "link" }],
  };
}
